import { useReducer } from 'react';
import { DEFAULT_AI_MODEL } from 'utils/aiModels';

import type { Lane, PlaygroundAction, PlaygroundState } from '../types';
import { newUid } from '../utils/turnMapping';

export const createInitialState = (): PlaygroundState => ({
  source: 'blank',
  ready: false,
  businessUnitId: null,
  promptIsFallback: false,
  prompt: '',
  functions: [],
  turns: [],
  model: DEFAULT_AI_MODEL,
  toolChoice: 'auto',
  mode: 'single',
  primaryRun: { status: 'idle' },
  groundTruth: undefined,
  compareLanes: [],
  rawConversationOpen: false,
  rawFunctionsOpen: false,
  conversationError: false,
  functionsError: false,
  dirty: false,
});

const makeLane = (model: string): Lane => ({
  id: newUid(),
  model,
  status: 'idle',
  isGroundTruth: false,
});

/** Map over compareLanes, updating the lane whose id matches. */
const patchLane = (lanes: Lane[], id: string, patch: Partial<Lane>): Lane[] =>
  lanes.map((lane) => (lane.id === id ? { ...lane, ...patch } : lane));

export const playgroundReducer = (state: PlaygroundState, action: PlaygroundAction): PlaygroundState => {
  switch (action.type) {
    case 'LOAD_FROM_MESSAGE':
      return {
        ...state,
        source: 'message',
        ready: true,
        promptIsFallback: false,
        prompt: action.payload.prompt,
        functions: action.payload.functions,
        turns: action.payload.turns,
        model: action.payload.model,
        groundTruth: action.payload.groundTruth,
        compareLanes: [],
        primaryRun: { status: 'idle' },
        dirty: false,
      };

    case 'LOAD_BU_PROMPT':
      return {
        ...state,
        source: 'blank',
        ready: true,
        businessUnitId: action.payload.businessUnitId,
        prompt: action.payload.prompt,
        functions: action.payload.functions,
        model: action.payload.model || state.model,
        promptIsFallback: action.payload.promptIsFallback,
        turns: [],
        groundTruth: undefined,
        compareLanes: [],
        primaryRun: { status: 'idle' },
        dirty: false,
      };

    case 'SET_BUSINESS_UNIT':
      return { ...state, businessUnitId: action.businessUnitId, ready: false };

    case 'SET_PROMPT':
      return { ...state, prompt: action.prompt, dirty: true };

    case 'SET_MODEL':
      return { ...state, model: action.model };

    case 'SET_TOOL_CHOICE':
      return { ...state, toolChoice: action.toolChoice };

    case 'ADD_TURN':
      return {
        ...state,
        turns: [...state.turns, { uid: newUid(), role: action.role, content: '' }],
        dirty: true,
      };

    case 'UPDATE_TURN':
      return {
        ...state,
        turns: state.turns.map((turn) => (turn.uid === action.uid ? { ...turn, ...action.patch } : turn)),
        dirty: true,
      };

    case 'DELETE_TURN':
      return { ...state, turns: state.turns.filter((turn) => turn.uid !== action.uid), dirty: true };

    case 'APPEND_TURN':
      return { ...state, turns: [...state.turns, action.turn], dirty: true };

    case 'SET_TURNS':
      return { ...state, turns: action.turns, conversationError: false, dirty: true };

    case 'SET_CONVERSATION_ERROR':
      return { ...state, conversationError: action.error };

    case 'SET_FUNCTIONS':
      return { ...state, functions: action.functions, functionsError: false, dirty: true };

    case 'SET_FUNCTIONS_ERROR':
      return { ...state, functionsError: action.error };

    case 'TOGGLE_RAW_CONVERSATION':
      return { ...state, rawConversationOpen: action.open };

    case 'TOGGLE_RAW_FUNCTIONS':
      return { ...state, rawFunctionsOpen: action.open };

    case 'SET_MODE':
      return { ...state, mode: action.mode };

    case 'PRIMARY_RUN_START':
      return { ...state, primaryRun: { status: 'running' } };

    case 'PRIMARY_RUN_SUCCESS':
      return { ...state, primaryRun: { status: 'done', result: action.result } };

    case 'PRIMARY_RUN_ERROR':
      return { ...state, primaryRun: { status: 'error', error: action.error } };

    case 'ADD_LANE':
      return { ...state, compareLanes: [...state.compareLanes, makeLane(action.model)] };

    case 'REMOVE_LANE':
      return { ...state, compareLanes: state.compareLanes.filter((lane) => lane.id !== action.id) };

    case 'SET_LANE_MODEL':
      return {
        ...state,
        compareLanes: patchLane(state.compareLanes, action.id, {
          model: action.model,
          // Changing the model invalidates any prior replay for that lane.
          status: 'idle',
          turns: undefined,
          progress: undefined,
          cost: undefined,
          time: undefined,
          error: undefined,
        }),
      };

    case 'PATCH_LANE':
      return { ...state, compareLanes: patchLane(state.compareLanes, action.id, action.patch) };

    default:
      return state;
  }
};

export const usePlaygroundState = () => useReducer(playgroundReducer, undefined, createInitialState);
